import {
  Send,
  Calendar,
  MapPin,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Building2,
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type PurchaseRequest,
  PR_STATUS_LABELS,
  PR_STATUS_COLORS,
  PR_PRIORITY_LABELS,
  PR_PRIORITY_COLORS,
} from '@/features/procurement/types';

interface PurchaseRequestCardProps {
  readonly request: PurchaseRequest;
  readonly onApprove?: (requestId: string) => void;
  readonly onReject?: (requestId: string) => void;
  readonly onViewDetails?: (requestId: string) => void;
}

export function PurchaseRequestCard({
  request,
  onApprove,
  onReject,
  onViewDetails,
}: PurchaseRequestCardProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: request.currency,
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const isPending = request.status === 'pending_approval';
  const isUrgent = request.priority === 'urgent' || request.priority === 'high';

  return (
    <Card className={`hover:shadow-md transition-shadow ${isUrgent ? 'border-orange-200' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              isPending ? 'bg-amber-100' : 'bg-blue-100'
            }`}>
              <Send className={`h-5 w-5 ${isPending ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <CardTitle className="text-base">{request.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{request.requestNumber}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PR_PRIORITY_COLORS[request.priority]}`}>
              {PR_PRIORITY_LABELS[request.priority]}
            </span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PR_STATUS_COLORS[request.status]}`}>
              {PR_STATUS_LABELS[request.status]}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-1">
            <Building2 className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">{request.siteName}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">{request.deliveryZone}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 text-muted-foreground" />
            <span>Required: {formatDate(request.requiredByDate)}</span>
          </div>
          <div className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 text-muted-foreground" />
            <span>{formatCurrency(request.estimatedTotal)}</span>
          </div>
        </div>

        <div className="rounded-lg bg-muted/50 p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Line Items</p>
              <p className="font-semibold">{request.lines.length} items</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Requested By</p>
              <p className="text-sm font-medium">{request.createdByName}</p>
            </div>
          </div>
        </div>

        {request.status === 'rejected' && request.rejectionReason && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-800">Rejection Reason</p>
                <p className="text-sm text-red-700">{request.rejectionReason}</p>
              </div>
            </div>
          </div>
        )}

        {request.status === 'approved' && request.approvedByName && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-green-800">
                  Approved by {request.approvedByName}
                </p>
                {request.approvedAt && (
                  <p className="text-xs text-green-700">{formatDate(request.approvedAt)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onViewDetails?.(request.requestId)}
          >
            View Details
          </Button>
          {isPending && onApprove && onReject && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => onReject(request.requestId)}
              >
                Reject
              </Button>
              <Button size="sm" onClick={() => onApprove(request.requestId)}>
                <CheckCircle className="mr-1 h-4 w-4" />
                Approve
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
